import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, BookOpen, CheckCircle, Star } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import PiPayment from '../components/PiPayment';
import UpiPayment from '../components/UpiPayment';
import ReviewForm from '../components/ReviewForm';
import ReviewsList from '../components/ReviewsList';
import { courses, enrollments, wallet, reviews } from '../lib/api';
import { getUser } from '../lib/auth';

const PI_TO_INR = 356;

const CourseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [walletConfig, setWalletConfig] = useState(null);
  const [upiConfig, setUpiConfig] = useState(null);
  const [courseReviews, setCourseReviews] = useState([]);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pi');
  const [transactionId, setTransactionId] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [courseRes, walletRes, upiRes, reviewsRes] = await Promise.all([
        courses.getOne(id),
        wallet.getConfig(),
        wallet.getUpiConfig(),
        reviews.getCourseReviews(id),
      ]);
      setCourse(courseRes.data);
      setWalletConfig(walletRes.data);
      setUpiConfig(upiRes.data);
      setCourseReviews(reviewsRes.data);

      // Check if user is enrolled
      try {
        const myEnrollments = await enrollments.getMy();
        const enrollment = myEnrollments.data.find((e) => e.course_id === id);
        if (enrollment) {
          setIsEnrolled(true);
          setCanReview(enrollment.payment_verified);
        }

        // Check if user already reviewed
        const myReviews = await reviews.getMyReviews();
        const reviewed = myReviews.data.some((r) => r.course_id === id);
        setHasReviewed(reviewed);
      } catch (error) {
        console.log('User not enrolled');
      }
    } catch (error) {
      toast.error('Failed to load course details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      await enrollments.create({
        course_id: id,
        transaction_id: transactionId || null,
        payment_method: paymentMethod,
      });
      toast.success('Enrollment successful! Awaiting payment verification.');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enrollment failed');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gray-300 border-t-black rounded-full animate-spin" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Course not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50">
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Logo size="sm" />
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate('/courses')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Courses
        </button>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
          <div className="p-8 md:p-12">
            <div className="mb-8">
              <span className="inline-block px-4 py-2 bg-black text-white text-sm font-mono uppercase tracking-wider rounded-full mb-4">
                {course.level}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{course.title}</h1>
              <p className="text-lg text-gray-600 leading-relaxed">{course.description}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8 py-8 border-y border-gray-200">
              <div className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-gray-700 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Duration</p>
                  <p className="text-gray-600">{course.duration}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-6 h-6 text-gray-700 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Schedule</p>
                  <p className="text-gray-600">{course.schedule}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <BookOpen className="w-6 h-6 text-gray-700 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Level</p>
                  <p className="text-gray-600">{course.level}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Course Price</p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-4xl font-bold text-gray-900">{course.price_pi}</span>
                      <span className="text-2xl font-bold text-purple-600">π</span>
                    </div>
                    <span className="text-xl text-gray-400">or</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xl font-bold text-gray-500">INR</span>
                      <span className="text-4xl font-bold text-gray-900">{(course.price_pi * PI_TO_INR).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
                {!showPayment && (
                  <button
                    onClick={() => setShowPayment(true)}
                    className="px-8 py-3 bg-black text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors"
                    data-testid="proceed-payment-btn"
                  >
                    Proceed to Payment
                  </button>
                )}
              </div>
            </div>

            {showPayment && (
              <div className="space-y-6">
                <div className="flex gap-4 mb-4" data-testid="payment-method-selector">
                  <button
                    onClick={() => setPaymentMethod('pi')}
                    className={`flex-1 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                      paymentMethod === 'pi'
                        ? 'bg-purple-50 border-purple-500 text-purple-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                    }`}
                    data-testid="pay-with-pi-btn"
                  >
                    Pay with Pi ({course.price_pi} π)
                  </button>
                  <button
                    onClick={() => setPaymentMethod('upi')}
                    className={`flex-1 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                      paymentMethod === 'upi'
                        ? 'bg-green-50 border-green-500 text-green-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                    }`}
                    data-testid="pay-with-upi-btn"
                  >
                    Pay with UPI (INR {(course.price_pi * PI_TO_INR).toLocaleString('en-IN')})
                  </button>
                </div>

                {paymentMethod === 'pi' ? (
                  <PiPayment
                    walletAddress={walletConfig?.wallet_address}
                    qrCodeBase64={walletConfig?.qr_code_base64}
                    amount={course.price_pi}
                    courseName={course.title}
                  />
                ) : (
                  <UpiPayment
                    upiId={upiConfig?.upi_id}
                    qrCodeBase64={upiConfig?.qr_code_base64}
                    amountInr={course.price_pi * PI_TO_INR}
                    courseName={course.title}
                  />
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    placeholder="Enter your transaction ID"
                    data-testid="transaction-id-input"
                  />
                </div>

                <button
                  onClick={handleEnroll}
                  disabled={enrolling}
                  className="w-full py-4 bg-black text-white rounded-xl font-semibold text-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-enrollment-btn"
                >
                  {enrolling ? (
                    'Processing...'
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Confirm Enrollment
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Star className="w-8 h-8 text-yellow-400" />
            Course Reviews
          </h2>

          {canReview && !hasReviewed && (
            <div className="mb-8">
              <ReviewForm courseId={id} onSuccess={() => { loadData(); }} />
            </div>
          )}

          {hasReviewed && (
            <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-700 font-medium">✓ You have already reviewed this course</p>
            </div>
          )}

          {!canReview && !isEnrolled && (
            <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-700">Enroll in this course to leave a review</p>
            </div>
          )}

          {isEnrolled && !canReview && (
            <div className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-700">Your payment is pending verification. You can review after verification.</p>
            </div>
          )}

          <ReviewsList reviews={courseReviews} />
        </div>
      </div>
    </div>
  );
};

export default CourseDetailPage;
